/**
 * Backup Restore API client (Spec 201 — US4)
 *
 * Recovery-point timeline (assessed), nearest-point, launch (refuse/queue), progress.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 201-backup-management-overhaul
 */

import apiClient from '../services/api';

export type BackupEntityType = 'lakehouse' | 'warehouse';

export interface AssessedPoint {
  runId: string;
  entityType: BackupEntityType;
  entityId: string;
  pointTime: string;
  backupType: 'full' | 'incremental';
  restorable: boolean;
  notRestorableReason: string | null;
  sizeBytes: number | null;
  tableCount: number | null;
  // Spec 202 — immutability (WORM) visibility
  immutabilityState?: 'locked' | 'held' | 'expired' | 'none' | null;
  immutableUntil?: string | null;
  storageLegalHold?: boolean;
  // Spec 214 FR-035 — the per-run BOUND storage account (Spec 208) this point restores from.
  storageAccountName?: string | null;
  storageBindingState?: string | null;
  // Spec 212 T033 — from the run row: the mover that wrote this point and its copy check (null = not recorded).
  mover?: 'server_side' | 'streaming' | null;
  integrityVerified?: boolean | null;
  // Spec 212 T014 — how many of the point's files were checked against their source MD5, beside how many were copied.
  objectsHashVerified?: number | null;
  filesCompleted?: number | null;
}

export type LaunchOutcome =
  | { status: 'launched'; executionName: string }
  | { status: 'queued'; reason: string };

/** Spec 214 FR-009: a backend REFUSAL/error is rendered as a refusal — never "queued". */
export type UiLaunchOutcome = LaunchOutcome | { status: 'refused'; reason: string };

export interface RestoreProgress {
  status?: string;
  startTime?: string;
  endTime?: string;
  [k: string]: unknown;
}

export interface BackupTableInfo {
  tableName: string;
  rowCount: number | null;
  sizeBytes: number | null;
  deltaVersion: number | null;
  sourceRunId: string;
  sourcePointTime: string;
  changedInPoint: boolean;
}

/** The FULL table picture at a recovery point — delta-capture aware (anchor full + overlaid
 *  incrementals). `tables` carries each table's latest state at-or-before the point. */
export interface TableProjection {
  backupType: 'full' | 'incremental';
  anchoredByFull: boolean;
  fullRunId: string | null;
  fullPointTime: string | null;
  tables: BackupTableInfo[];
}

/** A protected entity (backup set) + recovery-point stats for the restore picker. */
export interface BackupSetSummary {
  entityType: BackupEntityType;
  entityId: string;
  name: string | null;
  workspaceId: string | null;
  workspaceName: string | null;
  envHint: 'prod' | 'dev' | 'uat' | 'test' | null;
  storageAccountName: string | null;
  backupCount: number;
  restorableCount: number;
  oldest: string | null;
  newest: string | null;
  fullCount: number;
  incrementalCount: number;
  pointsInRange: number;
}

/** A candidate destination workspace for a restore. */
export interface RestoreTarget {
  workspaceId: string;
  workspaceName: string;
}

type Wrapped<T> = { success: boolean; data: T };
const BASE = '/api/v1/backup/restore';

export const restoreApi = {
  recoveryPoints: async (
    entityType: BackupEntityType,
    entityId: string,
    from?: string,
    to?: string
  ): Promise<AssessedPoint[]> => {
    const q = new URLSearchParams({ entityType, entityId });
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    return (await apiClient.get<Wrapped<AssessedPoint[]>>(`${BASE}/recovery-points?${q.toString()}`)).data;
  },

  nearest: async (entityType: BackupEntityType, entityId: string, time: string): Promise<AssessedPoint | null> =>
    (await apiClient.get<Wrapped<AssessedPoint | null>>(
      `${BASE}/nearest?entityType=${entityType}&entityId=${entityId}&time=${encodeURIComponent(time)}`
    )).data,

  launch: async (
    entityType: BackupEntityType,
    entityId: string,
    runId: string,
    targetWorkspaceId?: string,
    tablesToRestore?: string[],
    targetItemId?: string
  ): Promise<LaunchOutcome> =>
    (await apiClient.post<Wrapped<LaunchOutcome>>(BASE, { entityType, entityId, runId, targetWorkspaceId, tablesToRestore, targetItemId })).data,

  progress: async (executionName: string): Promise<RestoreProgress | null> =>
    (await apiClient.get<Wrapped<RestoreProgress | null>>(`${BASE}/${executionName}/progress`)).data,

  testRestore: async (entityType: BackupEntityType, entityId: string, runId: string): Promise<LaunchOutcome> =>
    (await apiClient.post<Wrapped<LaunchOutcome>>(`${BASE}/test/${entityType}/${entityId}`, { runId })).data,

  tablesForRun: async (runId: string): Promise<TableProjection> =>
    (await apiClient.get<Wrapped<TableProjection>>(`${BASE}/recovery-points/${runId}/tables`)).data,

  restoreTargets: async (): Promise<RestoreTarget[]> =>
    (await apiClient.get<Wrapped<RestoreTarget[]>>(`${BASE}/restore-targets`)).data,

  backupSets: async (from?: string, to?: string): Promise<BackupSetSummary[]> => {
    const q = new URLSearchParams();
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    const qs = q.toString();
    return (await apiClient.get<Wrapped<BackupSetSummary[]>>(`${BASE}/backup-sets${qs ? `?${qs}` : ''}`)).data;
  },
};

// ── Spec 204: Instant & Granular Recovery types + API ─────────────────────

export type InplaceMountStatus = 'Provisioning' | 'Active' | 'Expiring' | 'Failed' | 'Deleted';

export interface InplaceMount {
  id: string;
  entityType: 'lakehouse';
  entityId: string;
  runId: string;
  targetWorkspaceId: string;
  shortcutItemId: string | null;
  shortcutName: string;
  owner: string;
  status: InplaceMountStatus;
  expiresAt: string;
  createdAt: string;
  deletedAt: string | null;
}

export interface PreflightCheck {
  name: string;
  passed: boolean;
  detail: string | null;
}

export interface PreflightResult {
  passed: boolean;
  checks: PreflightCheck[];
}

export interface TableBrowseEntry {
  tableName: string;
  schemaName: string;
  rowCount: number | null;
  sizeBytes: number | null;
  partitionColumns: string[] | null;
  parquetPath: string | null;
}

export interface RowSample {
  columns: string[];
  rows: unknown[][];
}

export const mountApi = {
  /** Create a query-in-place shortcut mount (US1). */
  create: async (
    entityType: 'lakehouse',
    entityId: string,
    runId: string,
    targetWorkspaceId: string,
    ttlHours?: number,
  ): Promise<InplaceMount> =>
    (await apiClient.post<Wrapped<InplaceMount>>(`${BASE}/mounts`, {
      entityType, entityId, runId, targetWorkspaceId, ttlHours,
    })).data,

  /** List active mounts for an entity. */
  list: async (entityType: string, entityId: string): Promise<InplaceMount[]> => {
    const r = await apiClient.get<Wrapped<{ mounts: InplaceMount[] }>>(
      `${BASE}/mounts?entityType=${entityType}&entityId=${entityId}`
    );
    return r.data.mounts;
  },

  /** Tear down a mount. */
  delete: async (mountId: string): Promise<void> => {
    await apiClient.delete(`${BASE}/mounts/${mountId}`);
  },

  /** Browse a recovery point's table list from its manifest (US3). */
  browse: async (entityType: BackupEntityType, runId: string): Promise<TableBrowseEntry[]> => {
    const r = await apiClient.get<Wrapped<{ tables: TableBrowseEntry[] }>>(
      `${BASE}/recovery-points/${runId}/browse?entityType=${entityType}`
    );
    return r.data.tables;
  },

  /** Sample rows for one table in a recovery point (US3 on-demand). */
  sample: async (entityType: BackupEntityType, runId: string, table: string): Promise<RowSample> =>
    (await apiClient.get<Wrapped<RowSample>>(
      `${BASE}/recovery-points/${runId}/sample?entityType=${entityType}&table=${encodeURIComponent(table)}`
    )).data,

  /** Pre-flight validate a restore before any write (US4). */
  preflight: async (
    entityType: BackupEntityType,
    entityId: string,
    runId: string,
    targetWorkspaceId: string,
    tablesToRestore?: string[],
  ): Promise<PreflightResult> =>
    (await apiClient.post<Wrapped<PreflightResult>>(`${BASE}/preflight`, {
      entityType, entityId, runId, targetWorkspaceId, tablesToRestore,
    })).data,
};
